import React, {useEffect, useMemo, useState} from 'react';
import {Box, LoadingDecorator} from '@renta-apps/renta-react-components';
import RentaLineChart, {ChartData} from '@/components/Charts/LineChart/RentaLineChart';
import {calculateMovingAverage} from '@/helpers/ChartDataHelper';
import {formatDate} from "@/helpers/DateHelper";
import RentaEasyConstants from '@/helpers/RentaEasyConstants';
import Localizer from '@/localization/Localizer';
import BasicChartModel from "@/models/server/BasicChartModel";
import {getEquipmentEmissionChartData} from '@/services/ConstructionSiteService';
import EasyPlusLink from "@/components/EasyPlus/EasyPlusLink/EasyPlusLink";
import {ch} from "@renta-apps/athenaeum-react-common";
import UserContext from "@/models/server/UserContext";
import {ConditionalEasyPlusRenderer} from "@/components/EasyPlus/EasyPlusConditionalRenderer/EasyPlusConditionalRenderer";

import styles from "./RentalCosts.module.scss";

interface RentalCostsProps {
    constructionSiteId: string;
}

const EquipmentEmissions: React.FC<RentalCostsProps> = ({constructionSiteId}) => {
    const [data, setData] = useState<ChartData[]>([]);
    const [loading, setLoading] = useState(true);

    const easyPlusEnabled = useMemo(() => {
        return (ch.getContext() as UserContext)?.isEasyPlusUser ?? false;
    }, []);


    useEffect(() => {
        if (!easyPlusEnabled) {
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            try {
                const chartData: BasicChartModel[] = await getEquipmentEmissionChartData(constructionSiteId);
                const dataWithMovingAverage = calculateMovingAverage(chartData, 6);

                setData(dataWithMovingAverage);
            } catch (error) {
                console.error('Error fetching co2 data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [constructionSiteId, easyPlusEnabled]); // Depend on easyPlusEnabled


    return (
        <LoadingDecorator className={styles.loadingContainer} hideContentOnLoading={true} isLoading={loading}>
            <ConditionalEasyPlusRenderer easyPlusEnabled={easyPlusEnabled}
                                         data={data}
                                         renderWhenHasData={(data) =>
                                             <RentaLineChart data={data}
                                                             xKey="date"
                                                             lines={[
                                                                 {
                                                                     dataKey: "value",
                                                                     dataName: Localizer.equipmentEmissionChartLegend,
                                                                     stroke: RentaEasyConstants.Colors.brightOrange,
                                                                     strokeWidth: 3,
                                                                     gradientId: "lineGradient",
                                                                     type: "linear",
                                                                 },
                                                                 {
                                                                     dataKey: "movingAverage",
                                                                     dataName: Localizer.genericChartMovingAverage,
                                                                     stroke: RentaEasyConstants.Colors.lightPurple,
                                                                     strokeWidth: 3,
                                                                     gradientId: "movingAverage",
                                                                     type: "monotone",
                                                                 },
                                                             ]}
                                                             title={`${data[data.length - 1]?.value.toFixed(2)} ${Localizer.equipmentEmissionChartTitle}`}
                                                             subtitle={`${formatDate(data[data.length - 1]?.date)}`}
                                                             legendItems={[
                                                                 {label: Localizer.equipmentEmissionChartLegend, color: "#FE5000"},
                                                                 {label: Localizer.genericChartMovingAverage, color: "#116CEA"},
                                                             ]}
                                                             xAxisFormatter={(date) => {
                                                                 const parsedDate = new Date(date);
                                                                 return `${(parsedDate.getMonth() + 1).toString().padStart(2, '0')}.${parsedDate.getFullYear().toString().slice(-2)}`;
                                                             }}
                                                             brush={true}
                                             />}
                                         renderWhenDisabled={() =>
                                             <Box className={styles.emptyConsumptionContainer}
                                                  display="flex"
                                                  flexDirection="column"
                                                  alignItems="center"
                                                  justifyContent="center">
                                                 <span data-cy={"powerRequireEasyPlus"}>{Localizer.easyPlusFeatureTitle}</span>
                                                 <EasyPlusLink>{Localizer.easyPlusInfoPanelReadMore}</EasyPlusLink>
                                             </Box>
                                         }
                                         renderWhenNoData={() =>
                                             <Box className={styles.emptyConsumptionContainer}
                                                  display="flex"
                                                  flexDirection="column"
                                                  alignItems="center"
                                                  justifyContent="center">
                                                 <span data-cy={"status-co2-emission-chart-no-data"}>{Localizer.genericNoData}</span>
                                             </Box>
                                         }></ConditionalEasyPlusRenderer>

        </LoadingDecorator>
    );
};

export default EquipmentEmissions;